/**
 * Per-item invoice settlement (PRD.244 follow-up — per-item pharmacy dispense).
 *
 * The authoritative per-item paid amount is derived in JS from real payment
 * allocations (invoice_payments.allocations) plus the "invoice fully paid ⇒
 * every item paid" rule — exactly as the read-only Item Payment Status panel
 * computes it. Pharmacy dispense eligibility, however, is enforced in pure SQL
 * across several queries, which can't run that Money-precise math.
 *
 * So we compute each item's status once here (on every payment/charge change)
 * and persist it to `invoice_items.payment_status` ('unpaid' | 'partial' |
 * 'paid'). Dispense queries then gate per item on that flag instead of only the
 * whole-invoice `invoices.status = 'paid'`.
 *
 * Call this after ANY change to an invoice's payments (add / edit / delete) or
 * its line charges (item add / remove / price edit). It's idempotent and only
 * writes rows whose status actually changed, so repeat calls and offline
 * replays don't churn the sync queue.
 */

import {
  aggregatePaymentAllocations,
  type InvoiceItemRow,
  type InvoicePaymentRow,
} from '@helios/data-access';
import { itemChargeCents } from '../pages/billing/payment-allocation-utils';

// Minimal PowerSync surface — matches the `db` passed around the app.
interface DbLike {
  getAll: <T>(sql: string, params?: unknown[]) => Promise<T[]>;
  execute: (sql: string, params?: unknown[]) => Promise<unknown>;
}

type ItemPaymentStatus = 'unpaid' | 'partial' | 'paid';

function statusOf(chargeCents: number, paidCents: number): ItemPaymentStatus {
  // A zero-charge line has nothing owed → settled.
  if (chargeCents <= 0 || paidCents >= chargeCents) return 'paid';
  if (paidCents > 0) return 'partial';
  return 'unpaid';
}

/**
 * Recompute and persist `payment_status` for every line on an invoice.
 * No-op when the invoice has no items (e.g. a just-deleted invoice).
 */
export async function recomputeInvoiceItemPaymentStatus(
  db: DbLike,
  invoiceId: string,
): Promise<void> {
  const items = await db.getAll<InvoiceItemRow>(
    `SELECT * FROM invoice_items WHERE invoice_id = ?`,
    [invoiceId],
  );
  if (items.length === 0) return;

  const payments = await db.getAll<InvoicePaymentRow>(
    `SELECT amount_cents, allocations FROM invoice_payments WHERE invoice_id = ?`,
    [invoiceId],
  );

  const knownIds = new Set(items.map((i) => i.id));
  // Item paid comes ONLY from explicit allocations (PRD.247 §5.5)...
  const { allocatedByItem, totalPaidCents } = aggregatePaymentAllocations(payments, knownIds);
  // ...except a fully-settled invoice (paid ≥ total charge) unambiguously
  // settles every item on it — detected by AMOUNT so a stale invoice.status
  // flag can't mislead.
  const invoiceChargeCents = items.reduce((s, it) => s + itemChargeCents(it), 0);
  const invoiceFullyPaid = invoiceChargeCents > 0 && totalPaidCents >= invoiceChargeCents;

  const now = new Date().toISOString();
  for (const it of items) {
    const chargeCents = itemChargeCents(it);
    const paidCents = invoiceFullyPaid
      ? chargeCents
      : Math.min(chargeCents, allocatedByItem.get(it.id) ?? 0);
    const next = statusOf(chargeCents, paidCents);
    // Only write when it actually changed — keeps the sync queue quiet.
    if ((it.payment_status ?? 'unpaid') !== next) {
      await db.execute(`UPDATE invoice_items SET payment_status = ?, updated_at = ? WHERE id = ?`, [
        next,
        now,
        it.id,
      ]);
    }
  }
}
